<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

import debounce from "lodash/debounce";

import { ADJUST_POSITION_DELAY } from "@/dsComponents/Dropdown";
import { computePosition } from "@/dsComponents/Dropdown/helpers";
import { DropdownDirections, type DropdownProps } from "@/dsComponents/Dropdown/types";

const props = withDefaults(defineProps<DropdownProps>(), {
    open: undefined,
    mode: "click",
    nostyle: false,
    width: "300px",
    maxWidth: "800px",
    maxHeight: "500px",
    height: "auto",
    direction: DropdownDirections.AUTOMATIC,
    disabled: false,
    debounce: 0,
});

const emit = defineEmits<{
    (event: "clickaway", value: MouseEvent): void;
    (event: "toggle", isOpen: boolean, customId: string): void;
    (event: "hover", isHovering: boolean): void;
}>();

const customId = computed(() => {
    const id = Math.round(Math.random() * 1000);
    return `dropdown-${String(id).padStart(3, "0")}`;
});

const isOpen = ref(false);
const clickArea = ref<HTMLDivElement>();
const dropdownArea = ref<HTMLDivElement>();
const positionLeft = ref(0);
const positionTop = ref(0);
const dropdown = ref<HTMLDivElement>();
const cancelLeaveEvent = ref<ReturnType<typeof setTimeout>>();
const timestamp = ref(0);

const adjustPosition = async (): Promise<void> => {
    const position = await computePosition(clickArea.value, dropdownArea.value, props.direction);
    if (position) {
        positionLeft.value = position.left;
        positionTop.value = position.top;
    }
};

const isDescendant = (event: Event): boolean => {
    const target = event.target;
    if (!target || !(target instanceof Element)) {
        return false;
    }
    const selector = `#ds-dropdowns .${customId.value}`;
    return target.closest(selector) !== null;
};

const handleChange = (event?: MouseEvent): void => {
    if (props.stop) {
        event?.stopPropagation();
    }
    isOpen.value = !isOpen.value;
    emit("toggle", isOpen.value, customId.value);
    adjustPosition();
};

const handleClick = (event: MouseEvent): void => {
    if (!isOpen.value && props.stop) {
        const customEvent = new CustomEvent("dropdown-close", { detail: { customId: customId.value } });
        document.dispatchEvent(customEvent);
    }
    if (props.disabled) {
        return;
    }
    if (props.mode === "click") {
        handleChange(event);
    }
};

const handleMouseEnter = debounce((): void => {
    if (props.mode !== "hover" || props.disabled) {
        return;
    }
    if (cancelLeaveEvent.value) {
        clearTimeout(cancelLeaveEvent.value);
    }
    isOpen.value = true;
    adjustPosition();
    emit("hover", true);
}, props.debounce);

const handleMouseLeave = (): void => {
    if (props.mode !== "hover") {
        return;
    }
    if (cancelLeaveEvent.value) {
        clearTimeout(cancelLeaveEvent.value);
    }
    cancelLeaveEvent.value = setTimeout(() => {
        isOpen.value = false;
    }, 120);
    handleMouseEnter.cancel();
    emit("hover", false);
};

const handleClickAway = (event: MouseEvent): void => {
    if (!dropdown.value || !isOpen.value) {
        return;
    }

    const triggeredBy = `.trigger.${customId.value}`;
    const descend = isDescendant(event);
    const target = event.target;
    const isTriggeredBy = target instanceof Element && target.closest(triggeredBy) !== null;

    if ((!descend || props.closeOnDescendant) && !isTriggeredBy) {
        handleChange();
        emit("clickaway", event);
    }
};

const handleDropdownClose = (event: Event): void => {
    const customEvent = event as CustomEvent<{ customId: string }>;
    if (customEvent?.detail?.customId === customId.value) {
        return;
    }
    if (isOpen.value) {
        isOpen.value = false;
    }
};

const handleScrollOrResize = (event: Event): void => {
    if (!isOpen.value) {
        return;
    }
    if (isDescendant(event)) {
        return;
    }
    if (performance.now() - timestamp.value < ADJUST_POSITION_DELAY) {
        return;
    }
    timestamp.value = performance.now();
    setTimeout(async () => {
        await adjustPosition();
    }, ADJUST_POSITION_DELAY);
};

watch(
    () => props.open,
    () => {
        isOpen.value = props.open ?? false;
    },
    { immediate: true }
);

watch(
    isOpen,
    async () => {
        if (isOpen.value) {
            await adjustPosition();
        }
    },
    { immediate: true }
);

onMounted(() => {
    document.addEventListener("click", handleClickAway);
    document.addEventListener("dropdown-close", handleDropdownClose);
    window.addEventListener("resize", handleScrollOrResize);
    document.addEventListener("scroll", handleScrollOrResize, true);
});

onUnmounted(() => {
    document.removeEventListener("click", handleClickAway);
    document.removeEventListener("dropdown-close", handleDropdownClose);
    window.removeEventListener("resize", handleScrollOrResize);
    document.removeEventListener("scroll", handleScrollOrResize, true);
    if (cancelLeaveEvent.value) {
        clearTimeout(cancelLeaveEvent.value);
    }
    handleMouseEnter.cancel();
});
</script>
<template>
    <div ref="dropdown" class="ds-dropdown-component" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
        <div ref="clickArea" :class="['trigger', customId, { fitContent }]" @click="handleClick">
            <slot name="trigger-zone" />
        </div>
        <Teleport v-if="!noShow" to="#ds-dropdowns">
            <div
                ref="dropdownArea"
                :id="customId"
                :class="['Dropdown', customId, direction, { nostyle }]"
                :style="`width: ${width}; --dropdown-position-y: ${positionTop}px; --dropdown-position-x: ${positionLeft}px; max-width: ${maxWidth};`"
                :data-debug="`top: ${positionTop}px, left: ${positionLeft}px`"
                v-show="isOpen"
                @mouseenter="handleMouseEnter"
                @mouseleave="handleMouseLeave"
            >
                <div class="content-container">
                    <div v-if="$slots['dropdown-header']" class="dropdown-header">
                        <slot name="dropdown-header" />
                    </div>
                    <div class="dropdown-content" :style="{ maxHeight, height, width, maxWidth, ...(styles ?? {}) }">
                        <slot name="dropdown-content" :adjust-position="adjustPosition" />
                    </div>
                    <div v-if="$slots['dropdown-footer']" class="dropdown-footer">
                        <slot name="dropdown-footer" />
                    </div>
                </div>
            </div>
        </Teleport>
    </div>
</template>
<style scoped lang="scss">
.Dropdown {
    position: absolute;
    // More then Overlay.vue component
    z-index: 99999;

    @include use-dropdown-animations;
    @include regular-shadow-md;
    top: var(--dropdown-position-y);
    left: var(--dropdown-position-x);

    &:not(.nostyle) .content-container {
        border-radius: var(--radius-md, 8px);
        border: 1px solid var(--stroke-soft-200, #e0e0e0);
        background-color: var(--bg-white, #ffffff);

        .dropdown-content {
            padding: var(--spacing-xs, 4px);
            height: 100%;
            max-height: 100%;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            gap: var(--spacing-xs, 4px);
        }

        .dropdown-footer {
            padding: 0 var(--spacing-md, 8px) var(--spacing-md, 8px);
        }
        .dropdown-header {
            padding: var(--spacing-md, 8px) var(--spacing-md, 8px) 0;
        }
    }
}

.ds-dropdown-component {
    height: fit-content;
    position: relative;

    .trigger {
        height: 100%;
        &.fitContent {
            width: fit-content;
        }
    }
}
</style>
